import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, User, Calendar, Edit, Trash2, Clock, Play, Square, Paperclip, Upload, Download, X, FileText, Image, File, Timer, Eye, ExternalLink, MessageSquare, Shield } from 'lucide-react';
import { toast } from 'sonner';
import TaskDialog from './TaskDialog';

const TaskList = ({ projectId, tasks, users, onTasksChange, isProjectCompleted = false, currentUserId = null }) => {
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // Timer state
  const [activeTimers, setActiveTimers] = useState({});
  const [timerDurations, setTimerDurations] = useState({});
  const timerIntervalRef = useRef({});
  
  // Time tracking totals per task
  const [taskTimeTotals, setTaskTimeTotals] = useState({});
  
  // Attachment state
  const [uploadingFor, setUploadingFor] = useState(null);
  const [taskAttachments, setTaskAttachments] = useState({});
  const fileInputRefs = useRef({});
  
  // Preview state
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Comment counts per task
  const [taskCommentCounts, setTaskCommentCounts] = useState({});
  // Unseen comment counts per task
  const [taskUnseenCounts, setTaskUnseenCounts] = useState({});

  // Fetch active timers, attachments, time totals, and comments on mount
  useEffect(() => {
    fetchActiveTimer();
    tasks.forEach(task => {
      fetchAttachments(task.id);
      fetchTaskTimeTotal(task.id);
      fetchCommentCount(task.id);
      fetchUnseenCommentCount(task.id);
    });
    
    // Listen for global timer stop event
    const handleTimerStopped = () => {
      // Clear all timers immediately
      Object.keys(timerIntervalRef.current).forEach(taskId => {
        clearInterval(timerIntervalRef.current[taskId]);
        delete timerIntervalRef.current[taskId];
      });
      setActiveTimers({});
      setTimerDurations({});
      // Refresh time totals
      tasks.forEach(task => fetchTaskTimeTotal(task.id));
    };
    
    // Listen for global timer start event
    const handleTimerStarted = (e) => {
      fetchActiveTimer();
    };
    
    window.addEventListener('timerStopped', handleTimerStopped);
    window.addEventListener('timerStarted', handleTimerStarted);
    
    return () => {
      window.removeEventListener('timerStopped', handleTimerStopped);
      window.removeEventListener('timerStarted', handleTimerStarted);
    };
  }, [tasks]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(timerIntervalRef.current).forEach(interval => clearInterval(interval));
    };
  }, []);

  const fetchCommentCount = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/comments`);
      setTaskCommentCounts(prev => ({ ...prev, [taskId]: response.data.length }));
    } catch (error) {
      console.error('Failed to fetch comments', error);
    }
  };

  const fetchUnseenCommentCount = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/comments/unseen-count`);
      setTaskUnseenCounts(prev => ({ ...prev, [taskId]: response.data.unseen_count }));
    } catch (error) {
      console.error('Failed to fetch unseen count', error);
    }
  };

  const fetchTaskTimeTotal = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/time-entries`);
      const entries = response.data || [];
      const totalMinutes = entries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
      setTaskTimeTotals(prev => ({ ...prev, [taskId]: totalMinutes }));
    } catch (error) {
      console.error('Failed to fetch time entries', error);
    }
  };

  const formatTotalTime = (minutes) => {
    if (!minutes || minutes === 0) return null;
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const fetchActiveTimer = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/active`);
      if (response.data && response.data.task_id) {
        const taskId = response.data.task_id;
        const startTime = new Date(response.data.start_time);
        
        // Only update if this is a new timer or different from current
        if (!activeTimers[taskId]) {
          setActiveTimers({ [taskId]: response.data });
          startDurationCounter(taskId, startTime);
        }
      } else {
        // No active timer - clear all timers
        Object.keys(timerIntervalRef.current).forEach(taskId => {
          clearInterval(timerIntervalRef.current[taskId]);
          delete timerIntervalRef.current[taskId];
        });
        setActiveTimers({});
        setTimerDurations({});
      }
    } catch (error) {
      // No active timer - clear all timers
      Object.keys(timerIntervalRef.current).forEach(taskId => {
        clearInterval(timerIntervalRef.current[taskId]);
        delete timerIntervalRef.current[taskId];
      });
      setActiveTimers({});
      setTimerDurations({});
    }
  };

  const startDurationCounter = (taskId, startTime) => {
    // Clear existing interval if any
    if (timerIntervalRef.current[taskId]) {
      clearInterval(timerIntervalRef.current[taskId]);
    }
    
    const updateDuration = () => {
      const now = new Date();
      const diff = Math.floor((now - startTime) / 1000);
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      setTimerDurations(prev => ({
        ...prev,
        [taskId]: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      }));
    };
    
    updateDuration();
    timerIntervalRef.current[taskId] = setInterval(updateDuration, 1000);
  };

  const fetchAttachments = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/attachments`);
      setTaskAttachments(prev => ({ ...prev, [taskId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch attachments', error);
    }
  };

  const handleStartTimer = async (taskId) => {
    try {
      const response = await axios.post(`${API}/time-entries/start?task_id=${taskId}`);
      setActiveTimers(prev => ({ ...prev, [taskId]: response.data }));
      startDurationCounter(taskId, new Date(response.data.start_time));
      // Dispatch global event to sync all timer displays
      window.dispatchEvent(new CustomEvent('timerStarted', { detail: response.data }));
      toast.success('Timer gestartet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Starten des Timers');
    }
  };

  const handleStopTimer = async (taskId) => {
    try {
      await axios.post(`${API}/time-entries/stop`);
      
      // Clear interval
      if (timerIntervalRef.current[taskId]) {
        clearInterval(timerIntervalRef.current[taskId]);
        delete timerIntervalRef.current[taskId];
      }
      
      setActiveTimers(prev => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
      setTimerDurations(prev => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
      
      // Dispatch global event to sync all timer displays
      window.dispatchEvent(new CustomEvent('timerStopped'));
      
      // Refresh total time for this task
      fetchTaskTimeTotal(taskId);
      
      toast.success('Zeit erfasst');
      onTasksChange();
    } catch (error) {
      toast.error('Fehler beim Stoppen des Timers');
    }
  };

  const handleFileUpload = async (taskId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu gross. Maximum: 10 MB');
      return;
    }

    setUploadingFor(taskId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/tasks/${taskId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Datei hochgeladen');
      fetchAttachments(taskId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingFor(null);
      if (fileInputRefs.current[taskId]) {
        fileInputRefs.current[taskId].value = '';
      }
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await axios.get(`${API}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    }
  };

  const isPreviewable = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf'].includes(ext);
  };

  const isImage = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };

  const handlePreview = async (attachment) => {
    const ext = attachment.original_filename.split('.').pop()?.toLowerCase();
    
    try {
      const response = await axios.get(`${API}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      
      if (ext === 'pdf') {
        // Open PDF in new tab
        window.open(url, '_blank');
      } else if (isImage(attachment.original_filename)) {
        // Show image in modal
        setPreviewAttachment(attachment);
        setPreviewUrl(url);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewAttachment(null);
    setPreviewUrl(null);
  };

  const handleDeleteAttachment = async (taskId, attachmentId) => {
    try {
      await axios.delete(`${API}/attachments/${attachmentId}`);
      toast.success('Anhang gelöscht');
      fetchAttachments(taskId);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <Image className="w-3 h-3 text-green-500" />;
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="w-3 h-3 text-blue-500" />;
    }
    return <File className="w-3 h-3 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleToggleTaskComplete = async (taskId, completed) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { completed: !completed });
      toast.success('Task Status aktualisiert');
      onTasksChange();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Tasks');
    }
  };

  const handleUpdateTaskCost = async (taskId, actualCost) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { actual_cost: parseFloat(actualCost) });
      toast.success('Kosten aktualisiert');
      onTasksChange();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Kosten');
    }
  };

  const handleUpdateManualCost = async (taskId, manualCost) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { manual_cost: parseFloat(manualCost) });
      toast.success('Kosten aktualisiert');
      onTasksChange();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Kosten');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Möchten Sie diesen Task wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task gelöscht');
      onTasksChange();
    } catch (error) {
      toast.error('Fehler beim Löschen des Tasks');
    }
  };

  const [defaultTab, setDefaultTab] = useState('details');

  const openEditDialog = (task, tab = 'details') => {
    setDefaultTab(tab);
    setEditingTask(task);
    setEditTaskDialogOpen(true);
  };

  const handleTaskCreated = () => {
    setTaskDialogOpen(false);
    onTasksChange();
  };

  const handleTaskUpdated = () => {
    setEditTaskDialogOpen(false);
    setEditingTask(null);
    setDefaultTab('details');
    onTasksChange();
    // Refresh comment counts
    tasks.forEach(task => {
      fetchCommentCount(task.id);
      fetchUnseenCommentCount(task.id);
    });
  };

  const handleCommentsSeen = async (taskId) => {
    // Mark comments as seen when user views them
    try {
      await axios.post(`${API}/tasks/${taskId}/comments/mark-seen`);
      setTaskUnseenCounts(prev => ({ ...prev, [taskId]: 0 }));
    } catch (error) {
      console.error('Failed to mark comments as seen', error);
    }
  };

  const isAnyTimerActive = Object.keys(activeTimers).length > 0;

  return (
    <>
      <Card data-testid="project-tasks-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tasks</CardTitle>
              <CardDescription>
                {isProjectCompleted 
                  ? 'Tasks können bei abgeschlossenen Projekten nicht bearbeitet werden'
                  : 'Verwalten Sie alle Tasks dieses Projekts'}
              </CardDescription>
            </div>
            {!isProjectCompleted && (
              <Button onClick={() => setTaskDialogOpen(true)} data-testid="create-task-button">
                <Plus className="w-4 h-4 mr-2" />
                Neuer Task
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Keine Tasks vorhanden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map(task => {
                const isTimerActive = !!activeTimers[task.id];
                const attachments = taskAttachments[task.id] || [];
                const canStartTimer = !isAnyTimerActive || isTimerActive;
                const totalTime = taskTimeTotals[task.id] || 0;
                const formattedTotalTime = formatTotalTime(totalTime);
                
                return (
                  <div
                    key={task.id}
                    className={`p-4 border rounded-lg transition-all ${
                      isTimerActive 
                        ? 'border-primary bg-primary/5 shadow-md' 
                        : 'border-border hover:shadow-md'
                    }`}
                    data-testid={`task-item-${task.id}`}
                  >
                    {/* Time Badge - Top Right */}
                    {(formattedTotalTime || isTimerActive) && (
                      <div className="flex justify-end mb-2 -mt-1">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          isTimerActive 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          <Timer className={`w-3 h-3 ${isTimerActive ? 'animate-pulse' : ''}`} />
                          {isTimerActive && (
                            <span className="font-mono">{timerDurations[task.id] || '00:00:00'}</span>
                          )}
                          {formattedTotalTime && (
                            <span className={isTimerActive ? 'opacity-70 ml-1' : ''}>
                              {isTimerActive ? `(Σ ${formattedTotalTime})` : formattedTotalTime}
                            </span>
                          )}
                          {!formattedTotalTime && !isTimerActive && (
                            <span>0m</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Main Task Row */}
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => handleToggleTaskComplete(task.id, task.completed)}
                        data-testid={`task-checkbox-${task.id}`}
                        disabled={isProjectCompleted}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className={`font-semibold ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </h4>
                          {/* Risk Mitigation Badge */}
                          {task.source === 'risk_mitigation' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                              <Shield className="w-3 h-3" />
                              Risiko
                            </span>
                          )}
                          {/* Priority Badge */}
                          {task.priority && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              task.priority === 'high' 
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                                : task.priority === 'medium' 
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}>
                              {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{task.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <div className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            {task.assigned_to_name}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {task.start_date && `${new Date(task.start_date).toLocaleDateString('de-CH')} - `}
                            {new Date(task.due_date).toLocaleDateString('de-CH')}
                          </div>
                        </div>
                      </div>
                      
                      {/* Cost Display */}
                      <div className="text-right shrink-0 min-w-[180px]">
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                          <span className="text-muted-foreground text-right">Zeitkosten:</span>
                          <span className="font-mono text-right">CHF {(task.actual_cost || 0).toFixed(2)}</span>
                          
                          <span className="text-muted-foreground text-right">Weitere:</span>
                          <Input
                            type="number"
                            step="0.01"
                            className="w-20 h-6 text-right text-xs p-1"
                            defaultValue={task.manual_cost || 0}
                            onBlur={(e) => {
                              const newValue = parseFloat(e.target.value) || 0;
                              if (newValue !== (task.manual_cost || 0)) {
                                handleUpdateManualCost(task.id, newValue);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            data-testid={`task-manual-cost-${task.id}`}
                            disabled={isProjectCompleted}
                          />
                          
                          <span className="text-muted-foreground text-right font-medium">Total:</span>
                          <span className="font-mono text-right font-medium">CHF {((task.actual_cost || 0) + (task.manual_cost || 0)).toFixed(2)}</span>
                          
                          <span className="text-muted-foreground text-right">Geplant:</span>
                          <span className="font-mono text-right text-muted-foreground">CHF {(task.planned_cost || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      {!isProjectCompleted && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(task)}
                            data-testid={`edit-task-${task.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTask(task.id)}
                            data-testid={`delete-task-${task.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Quick Actions Row */}
                    {!isProjectCompleted && (
                      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/50">
                        {/* Timer Controls */}
                        <div className="flex items-center gap-2">
                          {isTimerActive ? (
                            <>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleStopTimer(task.id)}
                                className="h-8"
                                data-testid={`stop-timer-${task.id}`}
                              >
                                <Square className="w-3 h-3 mr-1 fill-current" />
                                Stop
                              </Button>
                              <div className="font-mono text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                                {timerDurations[task.id] || '00:00:00'}
                              </div>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStartTimer(task.id)}
                              disabled={!canStartTimer}
                              className="h-8"
                              data-testid={`start-timer-${task.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Zeit erfassen
                            </Button>
                          )}
                        </div>
                        
                        {/* File Upload */}
                        <div className="flex items-center gap-2">
                          <input
                            ref={el => fileInputRefs.current[task.id] = el}
                            type="file"
                            onChange={(e) => handleFileUpload(task.id, e)}
                            className="hidden"
                            id={`file-upload-${task.id}`}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.zip,.csv"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRefs.current[task.id]?.click()}
                            disabled={uploadingFor === task.id}
                            className="h-8"
                            data-testid={`upload-file-${task.id}`}
                          >
                            {uploadingFor === task.id ? (
                              <>
                                <Upload className="w-3 h-3 mr-1 animate-pulse" />
                                Lädt...
                              </>
                            ) : (
                              <>
                                <Paperclip className="w-3 h-3 mr-1" />
                                Datei
                              </>
                            )}
                          </Button>
                          
                          {attachments.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {attachments.length} Anhang{attachments.length > 1 ? 'e' : ''}
                            </span>
                          )}
                        </div>

                        {/* Spacer to push comments to the right */}
                        <div className="flex-1" />

                        {/* Comments Button - Right side */}
                        {(taskCommentCounts[task.id] || 0) > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(task, 'comments')}
                            className={`h-8 relative ${
                              (taskUnseenCounts[task.id] || 0) > 0 
                                ? 'text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100' 
                                : 'text-muted-foreground hover:text-primary'
                            }`}
                            data-testid={`comments-btn-${task.id}`}
                          >
                            <MessageSquare className={`w-4 h-4 mr-1 ${(taskUnseenCounts[task.id] || 0) > 0 ? 'fill-orange-200' : ''}`} />
                            <span className="font-medium">{taskCommentCounts[task.id]}</span>
                            {(taskUnseenCounts[task.id] || 0) > 0 && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                                {taskUnseenCounts[task.id]}
                              </span>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Attachments List */}
                    {attachments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <div className="flex flex-wrap gap-2">
                          {attachments.map(attachment => (
                            <div
                              key={attachment.id}
                              className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded text-xs group hover:bg-muted transition-colors"
                            >
                              {getFileIcon(attachment.original_filename)}
                              <span 
                                className={`max-w-[120px] truncate ${isPreviewable(attachment.original_filename) ? 'cursor-pointer hover:text-primary' : ''}`}
                                onClick={() => isPreviewable(attachment.original_filename) && handlePreview(attachment)}
                                title={`${attachment.original_filename} (${formatFileSize(attachment.file_size)})`}
                              >
                                {attachment.original_filename}
                              </span>
                              {isPreviewable(attachment.original_filename) && (
                                <button
                                  onClick={() => handlePreview(attachment)}
                                  className="text-muted-foreground hover:text-primary"
                                  title="Vorschau"
                                >
                                  <Eye className="w-3 h-3" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDownload(attachment)}
                                className="text-muted-foreground hover:text-primary"
                                title="Herunterladen"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                              {!isProjectCompleted && (
                                <button
                                  onClick={() => handleDeleteAttachment(task.id, attachment.id)}
                                  className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Löschen"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Preview Modal */}
      <Dialog open={!!previewAttachment} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center justify-between pr-8">
              <span className="truncate">{previewAttachment?.original_filename}</span>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => previewAttachment && handleDownload(previewAttachment)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Herunterladen
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 pt-0 bg-muted/30">
            {previewUrl && (
              <img
                src={previewUrl}
                alt={previewAttachment?.original_filename}
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        projectId={projectId}
        users={users}
        tasks={tasks}
        onSuccess={handleTaskCreated}
        mode="create"
        currentUserId={currentUserId}
      />

      {/* Edit Task Dialog */}
      {editingTask && (
        <TaskDialog
          open={editTaskDialogOpen}
          onOpenChange={(open) => {
            setEditTaskDialogOpen(open);
            if (!open) setDefaultTab('details');
          }}
          projectId={projectId}
          users={users}
          tasks={tasks}
          task={editingTask}
          onSuccess={handleTaskUpdated}
          onCommentsChange={(taskId) => {
            fetchCommentCount(taskId);
            fetchUnseenCommentCount(taskId);
          }}
          onCommentsSeen={handleCommentsSeen}
          mode="edit"
          currentUserId={currentUserId}
          defaultTab={defaultTab}
        />
      )}
    </>
  );
};

export default TaskList;
